'use client';

import { useState, useEffect } from 'react';
import * as yaml from 'js-yaml';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Code } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { yamlToJsonContent } from '../../data/yaml-to-json-content';

const sampleYaml = `person:
  name: John Doe
  age: 30
  isActive: true
  contact:
    email: john@example.com
    phone: +1 (555) 123-4567
  address:
    street: 123 Main St
    city: New York
    country: USA
    coordinates: [40.7128, -74.0060]
  skills:
    - JavaScript
    - TypeScript
    - React
    - Node.js
  experience:
    - company: Tech Corp
      position: Senior Developer
      years: 5
    - company: Web Solutions
      position: Frontend Developer
      years: 3`;

const sampleJson = `{
  "person": {
    "name": "John Doe",
    "age": 30,
    "isActive": true,
    "contact": {
      "email": "john@example.com",
      "phone": "+1 (555) 123-4567"
    },
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "country": "USA",
      "coordinates": [40.7128, -74.006]
    },
    "skills": ["JavaScript", "TypeScript", "React", "Node.js"],
    "experience": [
      {
        "company": "Tech Corp",
        "position": "Senior Developer",
        "years": 5
      },
      {
        "company": "Web Solutions",
        "position": "Frontend Developer",
        "years": 3
      }
    ]
  }
}`;

function formatJson(jsonString: string): string {
    try {
        const obj = JSON.parse(jsonString);
        return JSON.stringify(obj, null, 2);
    } catch (e) {
        return jsonString;
    }
}

function yamlToJson(yamlString: string): string {
    try {
        if (!yamlString.trim()) {
            return '';
        }

        // Parse YAML to JavaScript object
        const jsonObj = yaml.load(yamlString);

        // Convert to formatted JSON string
        return JSON.stringify(jsonObj, null, 2);
    } catch (error) {
        throw new Error(`Invalid YAML: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

export default function YamlToJsonTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isPretty, setIsPretty] = useState(true);
    const { toast } = useToast();

    const convertYamlToJson = (yamlString: string) => {
        try {
            if (!yamlString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            const jsonString = yamlToJson(yamlString);
            setOutput(jsonString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid YAML'}`);
            setOutput('');
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            convertYamlToJson(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The JSON has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
    };

    const loadSample = () => {
        setInput(sampleYaml);
    };

    const toggleJsonFormatting = () => {
        if (output) {
            try {
                if (isPretty) {
                    // Minify JSON
                    const minified = JSON.stringify(JSON.parse(output));
                    setOutput(minified);
                } else {
                    // Pretty print JSON
                    const pretty = formatJson(output);
                    setOutput(pretty);
                }
                setIsPretty(!isPretty);
            } catch (e) {
                // Ignore errors
            }
        }
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="YAML to JSON Converter - Free Online Data Format Converter">
                Convert YAML data to JSON format instantly with our free online converter. Perfect for configuration files and API data.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        YAML Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Paste your YAML data here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your YAML here...\nperson:\n  name: John Doe\n  age: 30\n  isActive: true\n  skills:\n    - JavaScript\n    - TypeScript'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        JSON Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Your converted JSON will appear here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={toggleJsonFormatting}
                                        disabled={!output}
                                        className={cn(
                                            'gap-1.5',
                                            isPretty ? 'bg-primary/5' : ''
                                        )}
                                    >
                                        {isPretty ? (
                                            <FileText className="h-3.5 w-3.5" />
                                        ) : (
                                            <Code className="h-3.5 w-3.5" />
                                        )}
                                        {isPretty ? 'Minify' : 'Beautify'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve indentation
                                    )}
                                    placeholder="Your converted JSON will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your converted JSON will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={yamlToJsonContent.title}
                    description={yamlToJsonContent.description}
                    content={yamlToJsonContent.content}
                    faqs={yamlToJsonContent.faqs}
                    relatedTools={yamlToJsonContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
